import React, { useCallback, useState, useEffect } from "react";
import { Form, Modal } from "@components";
import { Box, Button } from "@material-ui/core";
import { Manager } from "@components/form/state/types";
import { SalesforceRoleModel } from "@api/types/salesforcerole";
import api from "@api";
import { useSalesforceRoleStore } from "../../../../../../store/hooksy";
import { alertService } from "@services";
import { REGIONS } from "../../../../../../screens/request/steps/summary/views/summary/additional-form-modal/forms/salesforce/details";

interface Props {
  open: boolean;
  onCancel: () => void;
  form: Manager<SalesforceRoleModel>;
}

type Key = keyof SalesforceRoleModel;

export const SalesforceRoleDetail: React.FC<Props> = ({
  open,
  onCancel,
  form,
}) => {
  const [, setSalesforceRoleStore] = useSalesforceRoleStore();

  let salesforceRole: SalesforceRoleModel = {
    id: 0,
    roleName: "",
    isActive: true,
    region: "",
  };

  function clearAndCloseSalesforceProfileModel() {
    salesforceRole.id = 0;
    salesforceRole.roleName = "";
    salesforceRole.isActive = false;
    salesforceRole.region = "";
    form.reset();
    onCancel();
  }

  const [isSubmitting, setSubmitting] = useState(false);

  const handleSubmit = useCallback(() => {
    Promise.all(Object.entries(form.values).map(validate)).then(
      (validations) => {
        const valid = validations.reduce((valid, [name, error]) => {
          return valid && !error;
        }, true);
        if (valid) {
          submitData();
        } else {
          validations.forEach(([name, error]) => {
            form.set(Form.target.ERROR, [name, error]);
          });
        }
      }
    );
  }, [form]);

  function submitData() {
    salesforceRole.id = form.values.id;
    salesforceRole.roleName = form.values.roleName;
    salesforceRole.isActive = form.values.isActive;
    salesforceRole.region = form.values.region;
    setSubmitting(true);
    if (salesforceRole.id !== 0) {
      // EDIT
      api.salesforcerole
        .putSalesforceRole(salesforceRole.id, salesforceRole)
        .then((response) => {
          alertService.success({
            msg: "Salesforce Role has been successfully updated.",
          });
          setSalesforceRoleStore({ fetchList: true });
          clearAndCloseSalesforceProfileModel();
        })
        .catch((error) => {
          if (error.response) {
            alertService.danger({ statusCode: error.response.status });
          }
        })
        .finally(() => {
          setSubmitting(false);
        });
    } else {
      // CREATE
      api.salesforcerole
        .postSalesforceRole(salesforceRole)
        .then((response) => {
          alertService.success({
            msg: "Salesforce Role has been successfully saved.",
          });
          setSalesforceRoleStore({ fetchList: true });
          clearAndCloseSalesforceProfileModel();
        })
        .catch((error) => {
          if (error.response) {
            alertService.danger({ statusCode: error.response.status });
          }
        })
        .finally(() => {
          setSubmitting(false);
        });
    }
  }

  function validate([name, value]: [string, any]): Promise<[Key, Error?]> {
    switch (name) {
      case "roleName":
      case "region":
        return Promise.resolve([name as Key, Form.Validators.hasValue(value)]);
      default:
        return Promise.resolve([name as Key]);
    }
  }

  function onChange(name: Key, value: any): void {
    form.set(Form.target.VALUE, [name, value]);
    validate([name, value]).then(([name, error]) => {
      form.set(Form.target.ERROR, [name, error]);
    });
  }

  const modalTitle = form.values.id !== 0 ? "Update" : "Add New";
  const submitText = isSubmitting ? "Submitting..." : "Submit";

  const regionOptions = [
    { label: "No Selection", value: "" },
    { label: REGIONS.AP, value: REGIONS.AP },
    { label: REGIONS.UK, value: REGIONS.UK },
    { label: REGIONS.CANADA, value: REGIONS.CANADA },
    { label: REGIONS.MNC, value: REGIONS.MNC },
    { label: REGIONS.CSP, value: REGIONS.CSP },
    { label: REGIONS.CSP_LATAM, value: REGIONS.CSP_LATAM },
    { label: REGIONS.MARKETING, value: REGIONS.MARKETING },
    { label: REGIONS.UK_ENTERPRISE, value: REGIONS.UK_ENTERPRISE },
    { label: REGIONS.MEXICO, value: REGIONS.MEXICO },
  ];

  return (
    <Modal
      isOpen={open}
      title={`${modalTitle} Salesforce Role`}
      closeModal={() => clearAndCloseSalesforceProfileModel()}
    >
      <Form>
        <Form.Textbox
          label="Role Name"
          name="roleName"
          onChange={onChange}
          size={12}
          error={form.errors.roleName}
          value={form.values.roleName}
          placeholder="Enter role name"
          required
        />
        <Form.Dropdown
          label="Region"
          name="region"
          onChange={onChange}
          size={7}
          value={form.values.region}
          error={form.errors.region}
          options={regionOptions}
        />
        <Form.Checkbox
          label="Is Active"
          name="isActive"
          onChange={onChange}
          size={6}
          value={form.values.isActive}
        />
      </Form>
      <Box textAlign="right" mt={4}>
        <Button
          variant="outlined"
          color="primary"
          onClick={() => clearAndCloseSalesforceProfileModel()}
        >
          Cancel
        </Button>
        <Button
          variant="contained"
          color="primary"
          type="button"
          onClick={handleSubmit}
          style={{ marginLeft: "40px" }}
          disabled={isSubmitting}
        >
          {submitText}
        </Button>
      </Box>
    </Modal>
  );
};
